import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
    BuildingOffice2Icon,
    IdentificationIcon,
    MapPinIcon,
    EnvelopeIcon,
    PhoneIcon,
    CheckBadgeIcon,
} from '@heroicons/react/24/outline';

export default function MyAccount() {
    const { user } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user?.email) {
            axios.get(`/api/v1/accounts/profile/me?email=${user.email}`)
                .then(res => setProfile(res.data))
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [user]);

    if (loading) return (
        <div className="flex items-center justify-center py-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#155e75]" />
        </div>
    );

    if (!profile) return (
        <div className="py-40 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">
            No Company Profile Linked to this Account.
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-fade-in py-6">
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Company Profile</h1>
                    <p className="text-sm text-gray-500 font-medium">Your National Registry Identity</p>
                </div>
            </div>

            <div className="glass-card bg-white rounded-none border border-gray-200 shadow-sm overflow-hidden">
                {/* Header Section */}
                <div className="bg-[#155e75] p-8 text-white relative">
                    <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-white/20 border border-white/40 rounded-sm text-[10px] font-black uppercase tracking-widest backdrop-blur-sm">
                            {profile.status}
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-white rounded-md shadow flex items-center justify-center text-[#155e75]">
                            <BuildingOffice2Icon className="w-10 h-10" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-black tracking-tight">{profile.account_name}</h2>
                            <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs font-bold text-cyan-100 flex items-center gap-1 uppercase tracking-widest">
                                    <IdentificationIcon className="w-4 h-4" /> REGC-{profile.registry_code}-{profile.postal_code || '000'}
                                </span>
                                <span className="text-xs font-bold text-cyan-100 flex items-center gap-1 uppercase tracking-widest">
                                    <CheckBadgeIcon className="w-4 h-4" /> {profile.account_type}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info Grid */}
                <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">

                    {/* Identity Elements */}
                    <div className="space-y-6">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">Business Identity</h3>

                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Legal Representative</p>
                            <p className="text-sm font-bold text-gray-800">{profile.legal_representative}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Entity Type</p>
                            <p className="text-sm font-bold text-gray-800">{profile.organization_type}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">VAT / Tax Identifier</p>
                            <p className="text-sm font-bold text-gray-800">{profile.vat_number || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">EECS Terms Acceptance</p>
                            <p className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                                <CheckBadgeIcon className="w-4 h-4" /> Signed & Verified
                            </p>
                        </div>
                    </div>

                    {/* Contact Elements */}
                    <div className="space-y-6">
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2">Contact & Location</h3>

                        <div className="flex gap-3">
                            <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Primary Email</p>
                                <p className="text-sm font-bold text-gray-800">{profile.email}</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <PhoneIcon className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone Number</p>
                                <p className="text-sm font-bold text-gray-800">{profile.phone || 'N/A'}</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <MapPinIcon className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Headquarters Address</p>
                                <p className="text-sm font-bold text-gray-800">{profile.street_address}</p>
                                <p className="text-sm font-bold text-gray-800">{profile.city}, {profile.postal_code}</p>
                                <p className="text-sm font-bold text-gray-800 mt-1 uppercase">{profile.country_code}</p>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="bg-gray-50 border-t border-gray-100 p-6 flex justify-end gap-3">
                    <button
                        onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'application/pdf';
                            input.onchange = (e) => {
                                if (e.target.files.length > 0) {
                                    alert(`Document "${e.target.files[0].name}" synchronized successfully. Awaiting National Issuing Body review.`);
                                }
                            };
                            input.click();
                        }}
                        className="px-6 py-2 bg-white border border-gray-200 text-[#155e75] text-[10px] font-black uppercase tracking-widest shadow-sm hover:bg-gray-50 transition-all"
                    >
                        Update Documents
                    </button>
                    <button
                        onClick={() => {
                            const html = `
                                <html>
                                  <head><title>Registry Prospectus - ${profile.account_name}</title></head>
                                  <body style="font-family: sans-serif; padding: 40px; color: #333;">
                                     <h1 style="color: #155e75;">Corporate Identity Prospectus</h1>
                                     <h2>${profile.account_name}</h2>
                                     <p><b>Legal Representative:</b> ${profile.legal_representative}</p>
                                     <p><b>Entity Type:</b> ${profile.organization_type}</p>
                                     <p><b>VAT Number:</b> ${profile.vat_number || 'N/A'}</p>
                                     <p><b>Registry Code:</b> REGC-${profile.registry_code}</p>
                                     <p><b>KYC Status:</b> ${profile.status}</p>
                                     <p><b>Generated:</b> ${new Date().toLocaleString()}</p>
                                     <hr style="margin-top: 40px; border: 1px solid #eee;" />
                                     <h3 style="color: #155e75; margin-top: 20px;">Asset & Ledger Declarations</h3>
                                     <p>To view your localized production plants, certificate volumes, and AIB Hub transfers, please execute a standard CSV export via the unified Registry Reporting interface.</p>
                                     <p style="font-size: 10px; color: #999; margin-top: 60px;">MAP GO Registry Sub-system - Confidential Profile Generation</p>
                                  </body>
                                </html>
                            `;
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(html);
                            printWindow.document.close();
                            printWindow.focus();
                            printWindow.print();
                        }}
                        className="px-6 py-2 bg-[#155e75] border border-[#155e75] text-white text-[10px] font-black uppercase tracking-widest shadow-sm hover:bg-[#0e4e61] transition-all"
                    >
                        Download Prospectus (PDF)
                    </button>
                </div>
            </div>
        </div>
    );
}
